import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, track, fileUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useBooking } from "@/context/BookingContext";
import { Button } from "@/components/ui/button";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import { Calendar, Users, Bike, ArrowRight } from "lucide-react";

const MOTO_IMG = "https://images.pexels.com/photos/38450768/pexels-photo-38450768.jpeg?auto=compress&cs=tinysrgb&w=1000";

export default function Eventi() {
  const { settings } = useSettings();
  const { open: openBooking } = useBooking();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    track("page_view", "eventi");
    track("event_view", "page");
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <SEO title="Eventi" description="Serate speciali, cene a tema, karaoke e menu stagionali al Ristorante Masetti di Zola Predosa." />
      <PageHero eyebrow="Serate ed eventi" title="Eventi da Masetti" cover={settings.eventi_cover} alt={settings.eventi_cover_alt} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <div className="grid md:grid-cols-2 rounded-2xl overflow-hidden border border-brass/20 bg-white shadow-sm" data-testid="event-card-motoraduno">
          <div className="h-56 md:h-full min-h-[14rem]">
            <img src={MOTO_IMG} alt="Motoraduno sui Colli Bolognesi con ritrovo da Masetti" loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div className="p-6 sm:p-8 flex flex-col justify-center">
            <span className="inline-flex items-center gap-1.5 self-start text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-bordeaux/10 text-bordeaux"><Bike className="w-3 h-3" />Motoraduno</span>
            <h3 className="font-serif text-2xl text-bordeaux mt-2">Un giro in moto sui Colli Bolognesi</h3>
            <p className="text-oak/80 mt-3 leading-relaxed">Giro turistico in moto sui colli, con ritrovo, partenza e ritorno al Ristorante Masetti. Al rientro, pranzo facoltativo alla carta.</p>
            <Link to="/motoraduno" onClick={() => track("cta_click", "eventi-motoraduno")} className="mt-5">
              <Button className="bg-bordeaux hover:bg-bordeaux-dark text-cream rounded-full px-6" data-testid="scopri-motoraduno-btn">Scopri il motoraduno <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {events.length === 0 ? (
          <p className="text-center text-oak/60 py-16">Al momento non ci sono eventi in programma. Torna a trovarci presto!</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((e) => (
              <div key={e.id} className="bg-white rounded-xl overflow-hidden border border-brass/15 shadow-sm flex flex-col" data-testid={`event-${e.id}`}>
                <div className="relative">
                  {e.image && <img src={fileUrl(e.image)} alt={e.image_alt || e.titolo} className="w-full h-52 object-cover" />}
                  {e.sold_out && <span className="absolute top-3 right-3 bg-bordeaux text-cream text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">Sold Out</span>}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-sm text-brass-hover"><Calendar className="w-4 h-4" />{e.date}</div>
                  <h3 className="font-serif text-xl text-oak mt-1">{e.titolo}</h3>
                  {e.descrizione && <p className="text-oak/70 mt-2 text-sm leading-relaxed flex-1">{e.descrizione}</p>}
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-oak/70">
                      {e.prezzo && <span className="font-serif text-bordeaux">€ {e.prezzo}</span>}
                      {e.posti && <span className="flex items-center gap-1 text-xs mt-0.5"><Users className="w-3 h-3" />{e.posti} posti</span>}
                    </div>
                    <Button disabled={e.sold_out} onClick={() => openBooking(`event-${e.id}`)}
                      className="bg-bordeaux hover:bg-bordeaux-dark text-cream rounded-full disabled:opacity-50" data-testid={`event-prenota-${e.id}`}>
                      {e.sold_out ? "Esaurito" : "Prenota"}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
